/**
 * Member directory page
 */
import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
} from "@mui/material";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import { authedClient } from "@smfa/utils/src";
import { get } from "lodash";
import { ContentCopy as CopyIcon } from "@mui/icons-material";

/**
 * Array of regex patterns for email addresses to ignore in the directory
 */
const ignoreEmailPatterns = [/^agentm.*test.*@gmail\.com$/, /^.*\+test\d*@.*$/];

/**
 * Country code to flag emoji mapping (simplified)
 */
const getCountryFlag = (countryCode) => {
  if (!countryCode) return "";
  // Convert country code to flag emoji
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/**
 * Member Directory - displays a list of members who have opted in to be listed
 * @returns {JSX.Element}
 * @component
 */
const MemberDirectory = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState("last_name");
  const [order, setOrder] = useState("asc");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Get user data for context
  // const user = useSelector((state) => state.user) || {};

  // Fetch member directory data
  useEffect(() => {
    const fetchMemberDirectory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authedClient.get("/user/member-directory");

        if (get(response, "data.status")) {
          setMembers(response.data.data || []);
        } else {
          setError(response.data.message || "Failed to load member directory");
        }
      } catch (err) {
        console.error("Error fetching member directory:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load member directory. Please try again later.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMemberDirectory();
  }, []);

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Filter members based on email patterns to ignore
  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const email = member.email || "";
      return !ignoreEmailPatterns.some((pattern) => pattern.test(email));
    });
  }, [members]);

  // Sort members
  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      let aValue = a[orderBy] || "";
      let bValue = b[orderBy] || "";

      // Handle case-insensitive string sorting
      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredMembers, orderBy, order]);

  // Copy email to clipboard
  const copyToClipboard = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy email:", err);
    }
  };

  // Format display name
  const formatDisplayName = (member) => {
    const parts = [];
    if (member.title) parts.push(member.title);
    if (member.first_name) parts.push(member.first_name);
    if (member.last_name) parts.push(member.last_name);
    return parts.join(" ");
  };

  // Format country display
  const formatCountry = (countryCode) => {
    if (!countryCode) return "";
    const flag = getCountryFlag(countryCode);
    return `${flag} ${countryCode.toUpperCase()}`;
  };

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageBanner mainTitle="Member Directory" dense={true} />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography
          variant="body1"
          color="text.secondary"
          align="center"
          sx={{ mb: 4 }}
        >
          Connect with fellow SMFA members. This directory includes members who
          have chosen to be listed publicly.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!error && sortedMembers.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No members are currently listed in the directory.
          </Alert>
        )}

        {!error && sortedMembers.length > 0 && (
          <Paper elevation={2}>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="member directory table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "country"}
                        direction={orderBy === "country" ? order : "asc"}
                        onClick={() => handleRequestSort("country")}
                      >
                        {" "}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "last_name"}
                        direction={orderBy === "last_name" ? order : "asc"}
                        onClick={() => handleRequestSort("last_name")}
                      >
                        Name
                      </TableSortLabel>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        Profession
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "institution"}
                        direction={orderBy === "institution" ? order : "asc"}
                        onClick={() => handleRequestSort("institution")}
                      >
                        Institution
                      </TableSortLabel>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          mt: 0.5,
                        }}
                      >
                        Specialty/Subspecialty
                      </Typography>
                    </TableCell>
                    <TableCell>E-mail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMembers.map((member, index) => (
                    <TableRow
                      key={`${member.email}-${index}`}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell
                        sx={{ padding: "8px 1px 8px 4px", width: "80px" }}
                      >
                        {member.country ? (
                          <Chip
                            label={formatCountry(member.country)}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" fontWeight="medium">
                          {formatDisplayName(member)}
                        </Typography>
                        {member.profession && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.5,
                            }}
                          >
                            {member.profession}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {member.institution || ""}
                        </Typography>
                        {member.specialty && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.5,
                            }}
                          >
                            {member.specialty}
                            {member.subspecialty && ` / ${member.subspecialty}`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={`mailto:${member.email}`}
                            sx={{
                              color: "primary.main",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {member.email}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(member.email)}
                            title="Copy email address"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedMembers.length} member
            {sortedMembers.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Email copied to clipboard!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </PageLayout>
  );
};

export default MemberDirectory;
